import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GenericRestService } from '../../offline-first';
import { DocumentRecord, DocumentRecordData } from '../../offline-first';
import { isArray, isString } from 'lodash';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { map, catchError, concatMap } from 'rxjs/operators';
import { ExerciseProgramDocument, Program } from '../models/exercise-program-document';
import { Plan, PlanDocument } from '../models/plan-document';
import { PlanGroup, PlanMetadata, PlanUser } from '../models/plan-metadata';
import { parseInt } from 'lodash';
import { UserLibraryDocument, User, UserLibrary } from '../models/user-library-document';
import { WorkoutSessionDocument, WorkoutSession } from '../models/workout-session-document';
import { ObservationDocument, Observation } from '../models/observation-document';
import { forkJoin } from 'rxjs';

const exerciseProgramBaseUrl = 'ExerciseProgramDocument';
const planDocumentBaseUrl = 'PlanDocument';
const workoutSessionDocumentBaseUrl = 'WorkoutSessionDocument';
const userLibraryBaseUrl = 'UserLibraryDocument';
const observationBaseUrl = 'ObservationDocument';

export class RestOptions {
  // tslint:disable-next-line: variable-name
  api_url: string;
}

interface DocumentApiResponse {
  uuid: string;
  document: string;
  version: number;
}

interface PlanDocumentsApiResponse {
  documentUUID: string;
  documentVersion: string;
  id: string;
  name: string;
  notes: string;
  createdByUserUUID: string;
  numWeeks: string;
  startDate: string;
  createdByUser: {
    userUUID: string;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
  };
  sharedWithUsers: {
    userUUID: string;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
  }[];
  publishedToGroups: {
    uuid: string;
    name: string;
  }[];
  reviewing: boolean;
}

function validateDocumentApiResponse(response: any) {
  return response.hasOwnProperty('uuid') && response.hasOwnProperty('document') && response.hasOwnProperty('version');
}

@Injectable({
  providedIn: 'root',
})
export class DocumentRestService extends GenericRestService {
  constructor(httpClient: HttpClient, environment: RestOptions) {
    super(httpClient);

    this.baseUrl = environment.api_url;
  }

  getExercisePrograms(): Observable<string[]> {
    return this.get(`${exerciseProgramBaseUrl}/`).pipe(
      map((response: { documents: string[] }) => {
        if (!response.hasOwnProperty('documents')) {
          throw new Error('GET api/ExerciseProgramDocument/: Invalid API response');
        }

        return response.documents;
      }),
    );
  }

  createExerciseProgramDocument(exerciseProgram: ExerciseProgramDocument): Observable<void> {
    return this.createDocument(exerciseProgram, exerciseProgramBaseUrl);
  }

  getExerciseProgram(uuid: string): Observable<ExerciseProgramDocument> {
    return this.getDocument<ExerciseProgramDocument, Program>(
      uuid,
      exerciseProgramBaseUrl,
      (data: DocumentRecordData<Program>) => new ExerciseProgramDocument(data),
    );
  }

  getExerciseProgramDiff(program: ExerciseProgramDocument): Observable<{ version: number; patch: object[] }> {
    return this.getDocumentDiff<ExerciseProgramDocument, Program>(program, exerciseProgramBaseUrl);
  }

  getExerciseProgramDocumentLatestVersion(uuid: string): Observable<number> {
    return this.get(`${exerciseProgramBaseUrl}/${uuid}/LatestVersion`).pipe(map(res => res.version));
  }

  patchExerciseProgramDocument(uuid: string, patch: any): Observable<number> {
    return this.patchDocument(uuid, exerciseProgramBaseUrl, patch);
  }

  getPlans(onlyPublished?: boolean): Observable<PlanMetadata[]> {
    return this.get(`${planDocumentBaseUrl}/${onlyPublished ? '?onlyPublishedPlans=true' : ''}`).pipe(
      concatMap(response => {
        if (!response.hasOwnProperty('plans')) {
          throw new Error(`GET api/${planDocumentBaseUrl}/: Invalid API response`);
        }

        if (response.plans.length === 0) {
          return of([]);
        }

        if (response.totalPages > 1) {
          const obs = [];
          for (let i = 2; i <= response.totalPages; i++) {
            obs.push(
              this.get(`${planDocumentBaseUrl}/?pageNumber=${i}&limitPerPage=${50}${onlyPublished ? '&onlyPublishedPlans=true' : ''}`)
            );
          }

          return forkJoin(obs).pipe(map((results) => {
            let plans = response.plans;
            results.forEach((result: any) => {
              if (!result.hasOwnProperty('plans')) {
                throw new Error(`GET api/${planDocumentBaseUrl}/: Invalid API response`);
              }

              if (result.plans.length > 0) {
                plans = plans.concat(result.plans);
              }
            });
            return plans.map(plan => this.buildPlanMetadata(plan));
          }));
        } else {
          return of(response.plans.map(plan => this.buildPlanMetadata(plan)));
        }
      }),
    );
  }

  getPlanSharedUsers(uuid: string): Observable<PlanUser[]> {
    return this.get(`${planDocumentBaseUrl}/${uuid}/getSharedUsers`).pipe(
      map(users => {
        if (users.hasOwnProperty('sharedWithUsers') && users.sharedWithUsers.length > 0) {
          return users.sharedWithUsers.map(user => {
            return {
              uuid: user.userUUID,
              email: user.userEmail,
              firstName: user.userFirstName,
              lastName: user.userLastName,
            } as PlanUser;
          });
        } else {
          return [];
        }
      }),
    );
  }

  getPlanPublishedGroups(uuid: string): Observable<PlanGroup[]> {
    return this.get(`Group/getGroupsWithPublishedPlanDocument/${uuid}`).pipe(
      map(response => {
        if (response.hasOwnProperty('groups') && response.groups.length > 0) {
          return response.groups.map(group => {
            return {
              uuid: group.uuid,
              name: group.name,
            } as PlanGroup;
          });
        } else {
          return [];
        }
      }),
    );
  }

  getGroupPublishedPlans(uuid: string): Observable<PlanMetadata[]> {
    return this.get(`Group/getPlanDocumentsPublishedToGroup/${uuid}`).pipe(
      map(response => {
        if (!response.hasOwnProperty('plans')) {
          throw new Error(`GET api/Group/getPlanDocumentsPublishedToGroup/${uuid} : Invalid API response`);
        }

        if (response.plans.length === 0) {
          return [];
        }

        return response.plans.map((plan: PlanDocumentsApiResponse) => this.buildPlanMetadata(plan));
      }),
    );
  }

  getPlanDocument(uuid: string): Observable<PlanDocument> {
    return this.getDocument<PlanDocument, Plan>(uuid, planDocumentBaseUrl, (data: DocumentRecordData<Plan>) => new PlanDocument(data));
  }

  getPlanDocumentDiff(plan: PlanDocument): Observable<{ version: number; patch: object[] }> {
    return this.getDocumentDiff<PlanDocument, Plan>(plan, planDocumentBaseUrl);
  }

  getPlanDocumentLatestVersion(uuid: string): Observable<number> {
    return this.get(`${planDocumentBaseUrl}/${uuid}/LatestVersion`).pipe(map(res => res.version));
  }

  createPlanDocument(plan: PlanDocument): Observable<void> {
    return this.createDocument(plan, planDocumentBaseUrl);
  }

  deletePlanDocument(uuid: string): Observable<void> {
    return this.deleteDocument(uuid, planDocumentBaseUrl);
  }

  patchPlanDocument(uuid: string, patch: any): Observable<number> {
    return this.patchDocument(uuid, planDocumentBaseUrl, patch);
  }

  getUserLibraryDocumentIds(): Observable<string[]> {
    return this.get(`${userLibraryBaseUrl}/`).pipe(
      map((response: { documents: string[] }) => {
        if (!response.hasOwnProperty('documents')) {
          throw new Error('GET api/UserLibraryDocument/: Invalid API response');
        }

        return response.documents;
      }),
    );
  }

  getUserLibraryDocument(uuid: string): Observable<UserLibraryDocument> {
    return this.getDocument(uuid, userLibraryBaseUrl, (data: DocumentRecordData<UserLibrary>) => new UserLibraryDocument(data));
  }

  getUserLibraryDocumentDiff(lib: UserLibraryDocument): Observable<{ version: number; patch: object[] }> {
    return this.getDocumentDiff<UserLibraryDocument, UserLibrary>(lib, userLibraryBaseUrl);
  }

  getUserLibraryDocumentLatestVersion(uuid: string): Observable<number> {
    return this.get(`${userLibraryBaseUrl}/${uuid}/LatestVersion`).pipe(map(res => res.version));
  }

  createUserLibraryDocument(plan: UserLibraryDocument): Observable<void> {
    return this.createDocument(plan, userLibraryBaseUrl);
  }

  deleteUserLibraryDocument(uuid: string): Observable<void> {
    return this.deleteDocument(uuid, userLibraryBaseUrl);
  }

  patchUserLibraryDocument(uuid: string, patch: any): Observable<number> {
    return this.patchDocument(uuid, userLibraryBaseUrl, patch);
  }

  sharePlan(uuid: string, userUUIDs: string[]): Observable<void> {
    return this.post(`${planDocumentBaseUrl}/${uuid}/share`, {
      userUUIDList: userUUIDs,
    }).pipe(map(() => null));
  }

  unsharePlan(uuid: string, userUUIDs: string[]): Observable<void> {
    return this.post(`${planDocumentBaseUrl}/${uuid}/unshare`, {
      userUUIDList: userUUIDs,
    }).pipe(map(() => null));
  }

  publishPlan(uuid: string, groupUUIDs: string[]): Observable<void> {
    return this.post(`Group/publishPlanDocument`, {
      plandocUUID: uuid,
      groupUUIDList: groupUUIDs,
    }).pipe(map(() => null));
  }

  unpublishPlan(uuid: string, groupUUIDs: string[]): Observable<void> {
    return this.post(`Group/unpublishPlanDocument`, {
      plandocUUID: uuid,
      groupUUIDList: groupUUIDs,
    }).pipe(map(() => null));
  }

  downloadPlaycard(planDocumentUUID: string, planDayId: string, version: number) {
    return this.post('DocumentXLSExport/', {
      planDocumentUUID,
      planDayId,
      version,
    }).pipe(
      map((res: any) => res.taskUUID),
      catchError(e => {
        return this.handleError(e);
      }),
    );
  }

  checkPlaycardTask(taskUUID: string) {
    return this.http
      .get(this.buildRoute('DocumentXLSExport/status/' + taskUUID), {
        responseType: 'blob',
      })
      .pipe(
        catchError(e => {
          return this.handleError(e);
        }),
      );
  }

  getWorkoutSessionDocumentIds(): Observable<string[]> {
    return this.get(`${workoutSessionDocumentBaseUrl}/`).pipe(
      map((response: { documents: string[] }) => {
        if (!response.hasOwnProperty('documents')) {
          throw new Error('GET api/WorkoutSessionDocument/: Invalid API response');
        }

        return response.documents;
      }),
    );
  }

  getWorkoutSessionDocument(uuid: string): Observable<WorkoutSessionDocument> {
    return this.getDocument<WorkoutSessionDocument, WorkoutSession>(
      uuid,
      workoutSessionDocumentBaseUrl,
      (data: DocumentRecordData<WorkoutSession>) => new WorkoutSessionDocument(data),
    );
  }

  createWorkoutSessionDocument(workoutSession: WorkoutSessionDocument): Observable<void> {
    return this.createDocument(workoutSession, workoutSessionDocumentBaseUrl);
  }

  getObservationDocumentIds(): Observable<string[]> {
    return this.get(`${observationBaseUrl}/`).pipe(
      map((response: { documents: string[] }) => {
        if (!response.hasOwnProperty('documents')) {
          throw new Error('GET api/ObservationDocument/: Invalid API response');
        }

        return response.documents;
      }),
    );
  }

  getObservationDocument(uuid: string): Observable<ObservationDocument> {
    return this.getDocument<ObservationDocument, Observation>(
      uuid,
      observationBaseUrl,
      (data: DocumentRecordData<Observation>) => new ObservationDocument(data),
    );
  }

  createObservationDocument(observation: ObservationDocument): Observable<void> {
    return this.createDocument(observation, observationBaseUrl);
  }

  private getDocument<TRecord extends DocumentRecord<TDocument>, TDocument>(
    uuid: string,
    baseUrl: string,
    init: (data: DocumentRecordData<TDocument>) => TRecord,
  ): Observable<TRecord> {
    return this.get(`${baseUrl}/${uuid}`).pipe(
      map((response: DocumentApiResponse) => {
        if (!validateDocumentApiResponse(response)) {
          throw new Error(`GET api/${baseUrl}/${uuid}: Invalid API response`);
        }

        const record = init({
          key: response.uuid,
          version: response.version,
          document: JSON.parse(response.document),
        });

        return record;
      }),
    );
  }

  private getDocumentDiff<TRecord extends DocumentRecord<TDocument>, TDocument>(
    record: TRecord,
    baseUrl: string,
  ): Observable<{ version: number; patch: object[] }> {
    return this.get(`${baseUrl}/${record.uuid}/Diff?fromVersion=${record.version}`).pipe(
      map(res => {
        if (res.hasOwnProperty('patchStream') && res.hasOwnProperty('toVersion') && isArray(res.patchStream)) {
          const patches = res.patchStream;
          let patch = [];
          patches.forEach(patchStr => {
            if (isString(patchStr)) {
              patch = patch.concat(JSON.parse(patchStr));
            }
          });

          return { version: res.toVersion, patch };
        }

        return { version: res.toVersion, patch: [] };
      }),
    );
  }

  private createDocument<TRecord extends DocumentRecord<TDocument>, TDocument>(record: TRecord, baseUrl: string): Observable<void> {
    return this.post(`${baseUrl}/${record.uuid}`, {
      document: JSON.stringify(record.document),
    }).pipe(map(() => null));
  }

  private deleteDocument(uuid: string, baseUrl: string): Observable<void> {
    return this.delete(`${baseUrl}/${uuid}`).pipe(map(() => null));
  }

  private patchDocument(uuid: string, baseUrl: string, patch: any): Observable<number> {
    return this.patch(`${baseUrl}/${uuid}`, {
      patchString: JSON.stringify(patch),
    }).pipe(map(res => res.version));
  }

  private buildPlanMetadata(plan: PlanDocumentsApiResponse) {
    const planMetadata = new PlanMetadata({
      key: plan.documentUUID,
    });

    planMetadata.name = plan.name;
    planMetadata.numWeeks = parseInt(plan.numWeeks, 10);
    planMetadata.notes = plan.notes;
    planMetadata.startDate = moment(plan.startDate);
    planMetadata.userUUID = plan.createdByUserUUID;
    planMetadata.version = parseInt(plan.documentVersion, 10);
    planMetadata.createdByUser = {
      email: plan.createdByUser.userEmail,
      firstName: plan.createdByUser.userFirstName,
      lastName: plan.createdByUser.userLastName,
      uuid: plan.createdByUser.userUUID,
    } as PlanUser;
    planMetadata.sharedWithUsers = plan.sharedWithUsers.map(user => {
      return {
        email: user.userEmail,
        firstName: user.userFirstName,
        lastName: user.userLastName,
        uuid: user.userUUID,
      } as PlanUser;
    });
    planMetadata.publishedToGroups = plan.publishedToGroups.map(group => {
      return {
        uuid: group.uuid,
        name: group.name,
      } as PlanGroup;
    });

    return planMetadata;
  }
}

